// src/components/EventForm/index.js
import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import useDateTimeSync from '../../hooks/useDateTimeSync';
import Button from '../Button/Button';
import { getRandomImageForEventType } from '../../utilities';
import { EVENT_TYPES, TERRAIN_TYPES, EXPERIENCE_LEVELS, TICKET_TYPES, TICKET_PAID, TICKET_FREE } from '../../constants/eventTypes';
import { VEHICLE_TYPES } from '../../constants/vehicleTypes';

// Subcomponentes
import ImageUploader from './ImageUploader';
import DescriptionSection from './DescriptionSection';
import DateTimeSection from './DateTimeSection';
import LocationSection from './LocationSection';
import OptionsSection from './OptionsSection';
import TitleSection from './TitleSection';

// Modales que se usan en las opciones
import CreateEventTypeModal from '../Modal/CreateEventTypeModal';
import CreateEventTerrainModal from '../Modal/CreateEventTerrainModal';
import CreateEventExperienceModal from '../Modal/CreateEventExperienceModal';
import CreateEventTicketModal from '../Modal/CreateEventTicketModal';
import CreateEventModalityModal from '../Modal/CreateEventModalityModal';
import CreateEventSelectOrganizerVehicleModal from '../Modal/CreateEventSelectVehicleModal';

const EventForm = forwardRef(({ initialData, onSubmit, isEditMode = false, loadingCreate }, ref) => {
  const { t } = useTranslation('createEvent');
  const autocompleteRef = useRef(null);

  // Hook para fechas y horas
  const {
    startDay,
    setStartDay,
    endDay,
    setEndDay,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    setIsEndDayChanged,
    setIsEndTimeChanged
  } = useDateTimeSync();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    shortLocation: '',
    eventType: EVENT_TYPES[0],
    terrain: TERRAIN_TYPES[0],
    experience: EXPERIENCE_LEVELS[0],
    tickets: isEditMode ? [] : [{
      name: t('createEventTicketModal.defaultTicketName'),
      type: TICKET_TYPES[0],
      price: 0,
      approvalRequired: false,
      capacity: 10
    }],
    approvalRequired: false,
    coordinates: { lat: null, lng: null },
    imageUrl: '',
    needsVehicle: true,
    organizerVehicle: null,
    allowedVehicleTypes: [VEHICLE_TYPES.MOTORCYCLE]
  });

  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [fileWarning, setFileWarning] = useState('');
  const [activeModal, setActiveModal] = useState(null);
  const [userSelectedImage, setUserSelectedImage] = useState(false);
  const [savedOptions, setSavedOptions] = useState({
    activity: false,
    ticket: false,
    modality: false,
    organizerVehicle: false,
    terrain: false,
    experience: false,
  });

  // Si hay datos iniciales para edición
  useEffect(() => {
    if (initialData && isEditMode) {
      setFormData(prev => ({
        ...prev,
        ...initialData,
        tickets: initialData.tickets
          ? initialData.tickets.map(ticket => ({
            ...ticket,
            approvalRequired: ticket.approvalRequired !== undefined ? ticket.approvalRequired : false,
          }))
          : [],
        imageUrl: initialData.image || '',
        coordinates: initialData.locationCoordinates
          ? {
            lat: initialData.locationCoordinates.coordinates[1],
            lng: initialData.locationCoordinates.coordinates[0],
          }
          : { lat: null, lng: null },
        needsVehicle: initialData.needsVehicle !== undefined ? initialData.needsVehicle : true,
        allowedVehicleTypes: Array.isArray(initialData.allowedVehicleTypes)
          ? initialData.allowedVehicleTypes
          : (initialData.needsVehicle === false ? [] : [VEHICLE_TYPES.MOTORCYCLE])
      }));
      // Normalizar las fechas para evitar problemas de timezone
      const startDateObj = new Date(initialData.startDate);
      const endDateObj = new Date(initialData.endDate);

      // Extraer componentes de fecha y hora respetando la zona horaria local
      const startDateStr = startDateObj.getFullYear() + '-' +
        String(startDateObj.getMonth() + 1).padStart(2, '0') + '-' +
        String(startDateObj.getDate()).padStart(2, '0');
      const startTimeStr = String(startDateObj.getHours()).padStart(2, '0') + ':' +
        String(startDateObj.getMinutes()).padStart(2, '0');

      const endDateStr = endDateObj.getFullYear() + '-' +
        String(endDateObj.getMonth() + 1).padStart(2, '0') + '-' +
        String(endDateObj.getDate()).padStart(2, '0');
      const endTimeStr = String(endDateObj.getHours()).padStart(2, '0') + ':' +
        String(endDateObj.getMinutes()).padStart(2, '0');

      setStartDay(startDateStr);
      setEndDay(endDateStr);
      setStartTime(startTimeStr);
      setEndTime(endTimeStr);
      // En modo edición, marcar que ya hay una imagen seleccionada
      if (initialData.image) {
        setUserSelectedImage(true);
      }
    }
  }, [initialData, isEditMode, setStartDay, setEndDay, setStartTime, setEndTime]);

  // Preseleccionar imagen aleatoria para nuevos eventos solo si el usuario no ha seleccionado una
  useEffect(() => {
    if (!isEditMode && !initialData && !userSelectedImage) {
      const randomImage = getRandomImageForEventType(formData.eventType);
      setFormData(prev => ({
        ...prev,
        imageUrl: randomImage
      }));
    }
  }, [isEditMode, initialData, formData.eventType, userSelectedImage]);

  // Validación (similar a la versión original)
  const validateForm = () => {
    const newErrors = {};
    const startDT = new Date(`${startDay}T${startTime}`);
    const endDT = new Date(`${endDay}T${endTime}`);
    const now = new Date();

    if (!formData.title.trim()) newErrors.title = t('eventForm.eventTitle.error');
    if (!formData.location.trim()) newErrors.location = t('eventForm.location.error');
    if (!formData.description.trim()) newErrors.description = t('createEventDescriptionModal.error');
    if (!file && !isEditMode && !formData.imageUrl) newErrors.file = t('eventForm.eventImage.error');

    // Validar que la fecha y hora de inicio no sea anterior al momento actual
    if (startDT <= now) {
      newErrors.startDay = t('eventForm.date.pastDate') || 'La fecha y hora de inicio debe ser posterior al momento actual';
      newErrors.startTime = t('eventForm.date.pastDate') || 'La fecha y hora de inicio debe ser posterior al momento actual';
    } else if (startDT >= endDT) {
      newErrors.startDay = t('eventForm.date.invalid') || `${t('eventForm.date.startLabel')} ${t('eventForm.date.endLabel')}`;
      newErrors.endDay = t('eventForm.date.invalid') || t('eventForm.date.endLabel');
    }
    if (!formData.coordinates.lat || !formData.coordinates.lng) {
      newErrors.location = t('eventForm.location.error');
    }
    if (!formData.tickets || !Array.isArray(formData.tickets) || formData.tickets.length === 0) {
      newErrors.tickets = t('eventForm.tickets.required');
    } else {
      formData.tickets.forEach((ticket, index) => {
        if (!ticket.name || !ticket.name.trim()) {
          newErrors[`tickets.${index}.name`] = t('eventForm.tickets.nameRequired', { index: index + 1 });
        }
        if (!ticket.type) {
          newErrors[`tickets.${index}.type`] = t('eventForm.tickets.typeRequired', { index: index + 1 });
        }
        if (ticket.type === TICKET_PAID) {
          const price = Number(ticket.price);
          if (isNaN(price) || price <= 0) {
            newErrors[`tickets.${index}.price`] = t('eventForm.tickets.priceInvalid', { index: index + 1 });
          }
        }
        const approvalValue = ticket.approvalRequired;
        if (approvalValue !== true && approvalValue !== false) {
          newErrors[`tickets.${index}.approvalRequired`] = t('eventForm.tickets.approvalInvalid', { index: index + 1 });
        }
        const capValue = Number(ticket.capacity);
        if (isNaN(capValue) || capValue < 1) {
          newErrors[`tickets.${index}.capacity`] = t('eventForm.tickets.capacityInvalid', { index: index + 1 });
        }
      });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useImperativeHandle(ref, () => ({
    getFormData,
    setInitialData: (data) => {
      setFormData(prev => ({
        ...prev,
        ...data,
        tickets: data.tickets || [{
          name: '',
          type: TICKET_FREE,
          price: 0,
          approvalRequired: false,
          capacity: 10
        }],
        imageUrl: data.image || '',
        coordinates: data.locationCoordinates
          ? {
            lat: data.locationCoordinates.coordinates[1],
            lng: data.locationCoordinates.coordinates[0],
          }
          : { lat: null, lng: null },
        needsVehicle: data.needsVehicle !== undefined ? data.needsVehicle : true,
        allowedVehicleTypes: Array.isArray(data.allowedVehicleTypes)
          ? data.allowedVehicleTypes
          : (data.needsVehicle === false ? [] : [VEHICLE_TYPES.MOTORCYCLE])
      }));
      const startDateObj = new Date(data.startDate);
      const endDateObj = new Date(data.endDate);
      setStartDay(startDateObj.toISOString().split('T')[0]);
      setEndDay(endDateObj.toISOString().split('T')[0]);
      setStartTime(startDateObj.toISOString().substr(11, 5));
      setEndTime(endDateObj.toISOString().substr(11, 5));
    }
  }));

  // Handlers para inputs y Autocomplete
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors({ ...errors, [name]: '' });
  };

  const handleFileChange = (e) => {
    // Manejar imágenes prediseñadas
    if (e.target.presetImageUrl) {
      setFormData(prev => ({ ...prev, imageUrl: e.target.presetImageUrl }));
      setFile(null);
      setFileWarning('');
      setErrors(prev => ({ ...prev, file: '' }));
      setUserSelectedImage(true); // Marcar que el usuario seleccionó una imagen
      return;
    }

    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validar tipo de archivo
    if (!selectedFile.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, file: t('eventForm.eventImage.supportedFiles') }));
      return;
    }

    // Avisar si el archivo es muy grande (mayor a 2 MB)
    if (selectedFile.size > 2 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, file: t('eventForm.eventImage.largeFile') }));
      return;
    }

    // Validar dimensiones y relación de aspecto
    const img = new Image();
    img.src = URL.createObjectURL(selectedFile);
    img.onload = () => {
      // Comprobar relación de aspecto 4:3 con una tolerancia
      const ratio = img.width / img.height;
      if (Math.abs(ratio - (4 / 3)) > 0.01) {
        // Avisar que la imagen se recortará para ajustarse a la proporción 4:3
        setFileWarning(t('eventForm.eventImage.warning'));
        // window.alert('La imagen tiene una relación de aspecto diferente a 4:3 y será recortada automáticamente.');
      }

      // Si se cumplen las condiciones, se limpia el error y se guarda el archivo
      setErrors(prev => ({ ...prev, file: '' }));
      setFile(selectedFile);
      // Limpiar la URL de imagen prediseñada si se selecciona un archivo
      setFormData(prev => ({ ...prev, imageUrl: '' }));
      setUserSelectedImage(true); // Marcar que el usuario seleccionó una imagen
    };

    // Limpiar el error previo (opcional)
    if (errors.file) {
      setErrors(prev => ({ ...prev, file: '' }));
    }
  };


  const onPlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (place && place.geometry) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setFormData(prev => ({
        ...prev,
        location: place.formatted_address,
        coordinates: { lat, lng }
      }));
      let locality = '', administrativeArea = '', country = '';
      place.address_components.forEach(component => {
        if (component.types.includes('locality')) locality = component.long_name;
        if (component.types.includes('administrative_area_level_2')) administrativeArea = component.long_name;
        if (component.types.includes('country')) country = component.long_name;
      });
      const shortLocation = locality && administrativeArea && locality !== administrativeArea
        ? `${locality}, ${administrativeArea}, ${country}`
        : `${administrativeArea}, ${country}`;
      setFormData(prev => ({ ...prev, shortLocation }));
    } else {
      alert(t('eventForm.location.searchPlaceholder'));
    }
  };

  const handleClearLocation = () => {
    setFormData(prev => ({
      ...prev,
      location: '',
      shortLocation: '',
      coordinates: { lat: null, lng: null }
    }));
    setErrors(prev => ({ ...prev, location: '' }));
  };

  const handleSave = async () => {
    try {
      const formData = await getFormData();
      if (formData && onSubmit) {
        onSubmit(formData);
      }
    } catch (error) {
      console.error('Error al obtener los datos del formulario:', error);
    }
  };

  // Función interna para obtener los datos del formulario
  const getFormData = async () => {
    if (!validateForm()) {
      console.error('Errores en el formulario, no se puede enviar');
      return null;
    }
    const startDT = new Date(`${startDay}T${startTime}`).toISOString();
    const endDT = new Date(`${endDay}T${endTime}`).toISOString();
    const locationCoordinates = {
      type: 'Point',
      coordinates: [formData.coordinates.lng, formData.coordinates.lat]
    };
    const tickets = formData.tickets.map(ticket => ({
      name: ticket.name,
      type: ticket.type,
      price: ticket.type === TICKET_PAID ? Number(ticket.price) : 0,
      approvalRequired: ticket.approvalRequired,
      capacity: Number(ticket.capacity)
    }));
    const newFormData = new FormData();
    newFormData.append('title', formData.title);
    newFormData.append('description', formData.description);
    newFormData.append('location', formData.location);
    newFormData.append('shortLocation', formData.shortLocation);
    newFormData.append('startDate', startDT);
    newFormData.append('endDate', endDT);
    newFormData.append('eventType', formData.eventType);
    newFormData.append('terrain', Array.isArray(formData.terrain) ? formData.terrain.join(',') : formData.terrain);
    newFormData.append('experience', formData.experience);
    newFormData.append('tickets', JSON.stringify(tickets));
    newFormData.append('locationCoordinates', JSON.stringify(locationCoordinates));
    newFormData.append('needsVehicle', formData.needsVehicle);
    newFormData.append('allowedVehicleTypes', JSON.stringify(formData.allowedVehicleTypes));
    if (file) {
      newFormData.append('image', file);
    } else if (formData.imageUrl) {
      // Si no hay archivo pero sí hay una URL de imagen prediseñada, enviarla
      newFormData.append('presetImageUrl', formData.imageUrl);
    }
    if (formData.organizerVehicle) {
      newFormData.append('organizerVehicle', formData.organizerVehicle.id);
    }
    return newFormData;
  };


  const handleOpenModal = (modalId) => setActiveModal(modalId);
  const handleCloseModal = () => setActiveModal(null);

  return (
    <FormContainer>
      {/* <HeaderSection
        title={formData.title}
        onTitleChange={handleInputChange}
        error={errors.title}
        user={user}
      /> */}
      <FormWrapper>
        <Grid>
          <Details>
            <ImageSection>
              <ImageUploader
                file={file}
                imageUrl={formData.imageUrl}
                eventType={formData.eventType}
                onFileChange={handleFileChange}
                error={errors.file}
                warning={fileWarning}
              />
            </ImageSection>
          </Details>
          <Settings>
            <TitleSection
              title={formData.title}
              onTitleChange={handleInputChange}
              error={errors.title}
            />
            <DescriptionSection
              description={formData.description}
              onChange={handleInputChange}
              error={errors.description}
            />
            <DateTimeSection
              startDay={startDay}
              setStartDay={setStartDay}
              startTime={startTime}
              setStartTime={setStartTime}
              endDay={endDay}
              setEndDay={setEndDay}
              endTime={endTime}
              setEndTime={setEndTime}
              setIsEndDayChanged={setIsEndDayChanged}
              setIsEndTimeChanged={setIsEndTimeChanged}
              errors={{ startDay: errors.startDay, startTime: errors.startTime, endDay: errors.endDay, endTime: errors.endTime }}
              setErrors={setErrors}
            />
            <LocationSection
              location={formData.location}
              onLocationChange={handleInputChange}
              onPlaceChanged={onPlaceChanged}
              onClearLocation={handleClearLocation}
              autocompleteRef={autocompleteRef}
              coordinates={formData.coordinates}
              error={errors.location}
            />
            <OptionsSection
              formData={formData}
              isEditMode={isEditMode}
              handleOpenModal={handleOpenModal}
              errors={errors}
              savedOptions={savedOptions}
            />
            <ActionWraper>
              <Button
                size="l"
                sizeTablet="l"
                sizeMobile="l"
                $fullWidth
                $variant="default"
                onClick={handleSave}
                disabled={loadingCreate} // Use the passed prop
              >
                {loadingCreate ?
                  (isEditMode ? t('editEvent.saving', 'Guardando...') : t('createEventPage.creating')) :
                  (isEditMode ? t('editEvent.saveChanges', 'Guardar cambios') : t('createEventPage.create'))
                }
              </Button>

            </ActionWraper>

          </Settings>
        </Grid>
      </FormWrapper>
      {/* Los modales se renderizan según activeModal */}
      {/* Renderizado de los modales según el valor de activeModal */}
      {activeModal === 'eventType' && (
        <CreateEventTypeModal
          eventType={formData.eventType}
          onSave={(value) => {
            setFormData(prev => ({ ...prev, eventType: value }));
            setSavedOptions(prev => ({ ...prev, activity: true }));
          }}
          onClose={handleCloseModal}
        />
      )}
      {activeModal === 'terrain' && (
        <CreateEventTerrainModal
          terrain={formData.terrain}
          onSave={(value) => {
            setFormData(prev => ({ ...prev, terrain: value }));
            setSavedOptions(prev => ({ ...prev, terrain: true }));
          }}
          onClose={handleCloseModal}
        />
      )}
      {activeModal === 'experience' && (
        <CreateEventExperienceModal
          experience={formData.experience}
          onSave={(value) => {
            setFormData(prev => ({ ...prev, experience: value }));
            setSavedOptions(prev => ({ ...prev, experience: true }));
          }}
          onClose={handleCloseModal}
        />
      )}
      {activeModal === 'vehicleRequirement' && (
        <CreateEventModalityModal
          needsVehicle={formData.needsVehicle}
          allowedVehicleTypes={formData.allowedVehicleTypes}
          organizerVehicle={formData.organizerVehicle}
          onOpenSelectVehicle={() => setActiveModal('selectVehicle')}
          onSave={({ needsVehicle, allowedVehicleTypes }) => {
            setFormData(prev => ({ ...prev, needsVehicle, allowedVehicleTypes }));
            setSavedOptions(prev => ({ ...prev, modality: true }));
          }}
          onClose={handleCloseModal}
        />
      )}
      {activeModal === 'selectVehicle' && (
        <CreateEventSelectOrganizerVehicleModal
          onSave={(vehicle) => {
            setFormData(prev => ({ ...prev, organizerVehicle: vehicle }));
            setSavedOptions(prev => ({ ...prev, organizerVehicle: true }));
          }}
          selectedVehicle={formData.organizerVehicle}
          allowedVehicleTypes={formData.allowedVehicleTypes}
          onClose={handleCloseModal}
        />
      )}
      {activeModal === 'ticket' && (
        <CreateEventTicketModal
          ticketName={formData.tickets[0].name}
          ticketType={formData.tickets[0].type}
          ticketPrice={formData.tickets[0].price}
          capacity={formData.tickets[0].capacity}
          approvalRequired={formData.tickets[0].approvalRequired}
          onSave={(ticket) => {
            setFormData(prev => ({ ...prev, tickets: [ticket] }));
            setSavedOptions(prev => ({ ...prev, ticket: true }));
          }}
          onClose={handleCloseModal}
        />
      )}

    </FormContainer>
  );
});

export default EventForm;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const FormWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  padding-top: 74px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-column-gap: ${({ theme }) => theme.sizing.xl};
  max-width: 1024px;
  width: 100%;
  padding: ${({ theme }) => theme.sizing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    grid-gap: ${({ theme }) => theme.sizing.md};
    padding: ${({ theme }) => theme.sizing.sm};
  }
`;

const Details = styled.div`
  grid-column: span 6;
  // grid-area: 1 / 1 / 2 / 8;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.md};
`;

const ImageSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const Settings = styled.div`
  grid-column: span 6;
  // grid-area: 1 / 8 / 2 / 13;
  // border-radius: 16px;
  // border: 1px solid var(--border-default-weak, #DCDCDC);
  // height: fit-content;
`;

const ActionWraper = styled.div`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.sizing.sm} 0px ${({ theme }) => theme.sizing.lg} 0px;
`;

export { EventForm };
